import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, catchError, filter, finalize, map, of, switchMap, take } from 'rxjs';
import { DataFeedControlResult, DataFeedUpload } from '../../models/data-feed.model';
import { EtlMonitorService } from '../../services/etl-monitor.service';
import { DataFeedConfirmDialogComponent, DataFeedConfirmDialogData } from './data-feed-confirm-dialog/data-feed-confirm-dialog.component';
import { describeHttpError, formatCompact, uploadName } from './data-feed-ui';

/** pause: stop now, keep progress. resume: Start again from where it stopped. cancel: Stop for good. */
export type DataFeedRunAction = 'pause' | 'resume' | 'cancel';

interface ActionText {
  confirm: (name: string, upload: DataFeedUpload) => DataFeedConfirmDialogData;
  call: (service: EtlMonitorService, runId: string) => Observable<DataFeedControlResult>;
  accepted: string;
  failed: string;
}

const ACTIONS: Record<DataFeedRunAction, ActionText> = {
  pause: {
    confirm: name => ({
      title: 'Pause import',
      icon: 'pause_circle',
      message: `Pause ${name}? It stops within a few seconds and the next upload in the queue starts. Start it again later to continue from where it stopped.`,
      confirmText: 'Pause',
    }),
    call: (s, runId) => s.pauseDataFeedRun(runId),
    accepted: 'Pause requested.',
    failed: 'Could not pause the import',
  },
  resume: {
    confirm: name => ({
      title: 'Start import',
      icon: 'play_circle',
      message: `Start ${name} again? It is queued and continues from where it stopped.`,
      confirmText: 'Start',
    }),
    call: (s, runId) => s.resumeDataFeedRun(runId),
    accepted: 'Start requested.',
    failed: 'Could not start the import',
  },
  cancel: {
    confirm: (name, upload) => ({
      title: 'Stop this import for good?',
      icon: 'warning',
      warn: true,
      message: `${name} will be cancelled and its saved progress discarded.${progressText(upload)}`,
      details: [
        'It cannot be started again. To load this file later, upload it again: it starts over from the first record.',
        'Records already loaded stay in the graph; a new upload overwrites them, it does not duplicate them.',
        upload.CanPause || upload.Stage === 'Paused'
          ? 'To free the queue and keep the progress, use Pause instead.'
          : 'To keep the progress, use Start instead: it continues from where it stopped.',
      ],
      acknowledge: 'I understand the progress of this import will be lost',
      alternative: upload.CanPause
        ? { text: 'Pause instead', value: 'pause' }
        : upload.CanResume ? { text: 'Start instead', value: 'resume' } : undefined,
      confirmText: 'Stop and discard progress',
    }),
    call: (s, runId) => s.cancelDataFeedRun(runId),
    accepted: 'Stop requested.',
    failed: 'Could not stop the import',
  },
};

/** " So far: 12.7K records loaded, 3 of 7 entities done." (empty when nothing was loaded yet). */
function progressText(upload: DataFeedUpload): string {
  const records = upload.RecordsSucceeded ?? 0;
  const parts: string[] = [];
  if (records > 0) {
    parts.push(`${formatCompact(records)} records loaded`);
  }
  if (upload.EntitiesTotal > 0 && upload.EntitiesDone > 0) {
    parts.push(`${upload.EntitiesDone} of ${upload.EntitiesTotal} entities done`);
  }
  return parts.length ? ` So far: ${parts.join(', ')}.` : '';
}

/**
 * Pause / Start / Stop for a data feed run, shared by the monitor table and the trace dialog:
 * confirm, call the API, then show the server's message in a snack bar.
 */
export class DataFeedRunControl {
  /** Run ids with a request in flight (drives the disabled state of the buttons). */
  private readonly inFlight = new Set<string>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly etlMonitorService: EtlMonitorService
  ) { }

  isBusy(runId: string | null | undefined): boolean {
    return !!runId && this.inFlight.has(runId);
  }

  /** The plain confirm dialog, for actions outside Pause / Start / Stop (closes with true when confirmed). */
  confirmDialog(data: DataFeedConfirmDialogData): Observable<boolean | string | undefined> {
    return this.dialog.open<DataFeedConfirmDialogComponent, DataFeedConfirmDialogData, boolean | string>(DataFeedConfirmDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      data,
    }).afterClosed().pipe(take(1));
  }

  /**
   * Asks for confirmation, then sends the request. Emits once the request has finished
   * (accepted or not) so the caller can refresh; completes without emitting when cancelled.
   */
  run(action: DataFeedRunAction, upload: DataFeedUpload): Observable<DataFeedControlResult> {
    const runId = upload.RunId;
    if (!runId || this.inFlight.has(runId)) {
      return of();
    }
    const text = ACTIONS[action];

    return this.dialog.open<DataFeedConfirmDialogComponent, DataFeedConfirmDialogData, boolean | DataFeedRunAction>(DataFeedConfirmDialogComponent, {
      width: action === 'cancel' ? '560px' : '480px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      data: text.confirm(uploadName(upload), upload),
    }).afterClosed().pipe(
      take(1),
      filter(choice => choice === true || choice === 'pause' || choice === 'resume'),
      switchMap(choice => {
        if (choice !== true) {
          // The safer alternative offered by the Stop dialog: ask for it with its own confirmation.
          return this.run(choice as DataFeedRunAction, upload);
        }
        this.inFlight.add(runId);
        return text.call(this.etlMonitorService, runId).pipe(
          map(body => this.normalize(body)),
          catchError(err => of(this.toResult(err, text.failed))),
          map(result => {
            const message = result.Message || (result.Accepted ? text.accepted : 'The request was not accepted.');
            this.snackBar.open(message, 'Close', { duration: result.Accepted ? 4000 : 6000 });
            return { ...result, Message: message };
          }),
          finalize(() => this.inFlight.delete(runId))
        );
      })
    );
  }

  /** Reads the result body (PascalCase, tolerating camelCase). */
  private normalize(body: any): DataFeedControlResult {
    return {
      Accepted: !!(body?.Accepted ?? body?.accepted),
      Message: String(body?.Message ?? body?.message ?? ''),
      NotFound: !!(body?.NotFound ?? body?.notFound),
    };
  }

  /** 409 / 404 carry a DataFeedControlResult body; anything else becomes a generic message. */
  private toResult(err: any, fallback: string): DataFeedControlResult {
    const body = err?.error;
    if (body && typeof body === 'object' && typeof (body.Message ?? body.message) === 'string') {
      return { ...this.normalize(body), Accepted: false };
    }
    if (typeof body === 'string' && body.trim() && err?.status === 400) {
      return { Accepted: false, Message: `${fallback} (${body})`, NotFound: false };
    }
    return { Accepted: false, Message: describeHttpError(err, fallback), NotFound: err?.status === 404 };
  }
}
